import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, decimal, money, plain, today } from '../api'

/**
 * The underwriting result, live from the ledger: what was written, what has
 * been earned, what is held back for cover not yet run off, what claims have
 * cost — and the development triangle the IBNR reserve comes from, shown
 * rather than asserted, so an actuary can check the projection.
 */
export default function Report() {
  const { t } = useTranslation()
  const [asOf, setAsOf] = useState(today())
  // Empty is the whole book, which is what an actuary wants first.
  const [insurer, setInsurer] = useState('')

  const products = useQuery({ queryKey: ['products'], queryFn: api.products })
  const insurers = [...new Set((products.data?.data ?? []).map(p => p.insurer).filter(Boolean) as string[])].sort()

  const report = useQuery({
    queryKey: ['report', asOf, insurer],
    queryFn: () => api.report({ as_of: asOf, insurer }),
    enabled: !!asOf,
  })

  // The statement reads as a statement: premium, then claims, then what the
  // two leave. A flat list of nine figures makes the reader do that grouping
  // in their head, and the subtotal is the line they are looking for.
  type Line = { label: string; value: number; kind?: 'head' | 'sum' | 'total' }
  const s = report.data?.statement
  const lines: Line[] = s ? [
    { label: t('premiumBlock'), value: 0, kind: 'head' },
    { label: t('writtenPremium'), value: s.written_premium },
    { label: t('earnedPremium'), value: s.earned_premium },
    { label: t('unearnedPremium'), value: s.unearned_premium },
    { label: t('claimsBlock'), value: 0, kind: 'head' },
    { label: t('paidClaims'), value: s.paid_claims },
    { label: t('outstandingClaims'), value: s.outstanding_claims },
    { label: t('ibnr'), value: s.ibnr },
    { label: t('totalClaims'), value: s.total_claims, kind: 'sum' },
    { label: t('resultBlock'), value: 0, kind: 'head' },
    { label: t('operatingExpenses'), value: s.operating_expenses },
    { label: t('margin'), value: s.underwriting_margin, kind: 'total' },
  ] : []

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('underwriting')}</span></div>
        <div className="panel-body">
          <div className="cards">
            <div className="field">
              <label htmlFor="asof">{t('asOf')}</label>
              <div className="input-wrap">
                <input id="asof" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="insurer">{t('insurerFilter')}</label>
              <div className="input-wrap">
                <select id="insurer" value={insurer} onChange={e => setInsurer(e.target.value)}
                  style={{ width: '100%', padding: '11px 12px', background: 'var(--surface-2)', color: 'var(--text)',
                           border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', fontWeight: 600 }}>
                  <option value="">{t('allInsurers')}</option>
                  {insurers.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {report.isLoading && <div className="skeleton" style={{ height: 220 }} />}
      {report.isError && <p className="notice">{(report.error as Error).message}</p>}

      {report.data && (
        <>
          <div className="panel">
            <div className="panel-head">
              <span className="grow">{t('asOf')} {report.data.as_of}</span>
              <span className="chip">{t('policiesCount')} {report.data.policies}</span>
              <span className="chip">{t('claimsCount')} {report.data.claims}</span>
            </div>
            <div className="panel-body flush">
              <table className="table">
                <tbody>
                  {lines.map(l => (
                    <tr key={l.label} className={l.kind ?? ''}>
                      <td>{l.label}</td>
                      <td className={`num${l.kind === 'head' ? '' : l.value < 0 ? ' neg' : ''}`}>
                        {l.kind === 'head' ? '' : money(l.value)}
                      </td>
                    </tr>
                  ))}
                  {report.data.ledger_stable !== undefined && (
                    <tr className="foot"><td>{t('ledgerCash')}</td><td className="num">{money(report.data.ledger_stable)}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head"><span className="grow">{t('triangle')}</span><span className="chip">₮</span></div>
            <div className="panel-body flush scroll-x">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('policyAge')}</th>
                    {report.data.triangle.developments.map(d => <th key={d} className="num">{d}</th>)}
                    <th className="num">{t('reportedTo')}</th>
                    <th className="num">{t('ultimate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.triangle.rows.map(r => (
                    <tr key={r.age}>
                      <td className="num">{r.age}</td>
                      {r.cumulative.map((v, i) => (
                        // past the diagonal the figures are projected, not observed
                        <td key={i} className={`num${(i + 1) * 10 > r.age ? ' muted' : ''}`}>{plain(Math.round(v))}</td>
                      ))}
                      <td className="num">{plain(Math.round(r.reported))}</td>
                      <td className="num">{plain(Math.round(r.ultimate))}</td>
                    </tr>
                  ))}
                  <tr>
                    <td>{t('factor')}</td>
                    {report.data.triangle.factors.map((f, i) => (
                      <td key={i} className="num muted">{decimal(f, 3)}</td>
                    ))}
                    <td /><td />
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="panel-body"><p className="field-hint">{t('ibnrNote')}</p></div>
          </div>
        </>
      )}
    </div>
  )
}
